import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import ConfirmModal from "../../components/ui/Modal/ConfirmModal.jsx";
import { useApp } from "../../hooks/useApp.js";
import { listTaxPreparerOptions, getTaxPreparer, deleteTaxPreparer } from "../../services/taxPreparerService.js";

const PAGE_SIZE = 10;

const TaxPreparers = () => {
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const [preparers, setPreparers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    let cancelled = false;
    listTaxPreparerOptions()
      .then((list) => {
        if (!cancelled) setPreparers(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const addPreparer = () => {
    dispatch({ type: "SEED", which: "new" });
    navigate("/dashboard/preparers/form");
  };

  const editPreparer = async (option) => {
    setEditingId(option.id);
    try {
      const full = await getTaxPreparer(option.id);
      dispatch({ type: "SEED", which: "new" });
      dispatch({ type: "SET_PREPARER", patch: full });
      dispatch({ type: "SET_PREPARER_ID", preparerId: option.id });
      navigate("/dashboard/preparers/form");
    } finally {
      setEditingId(null);
    }
  };

  const removePreparer = (option) => {
    setDeleteError("");
    setPendingDelete(option);
  };

  const cancelDeletePreparer = () => {
    setPendingDelete(null);
    setDeleteError("");
  };

  const confirmDeletePreparer = async () => {
    const option = pendingDelete;
    if (!option) return;
    setDeletingId(option.id);
    try {
      await deleteTaxPreparer(option.id);
      setPreparers((prev) => prev.filter((p) => p.id !== option.id));
      setPage((p) => {
        const remaining = preparers.length - 1;
        const maxPage = Math.max(0, Math.ceil(remaining / PAGE_SIZE) - 1);
        return Math.min(p, maxPage);
      });
      setPendingDelete(null);
      setDeleteError("");
    } catch (err) {
      setDeleteError(err.message || "Couldn't delete this preparer. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="flex justify-between items-end mb-5 gap-4 flex-wrap">
        <div>
          <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
            Preparers
          </div>
          <h1 className="text-[28px] leading-[1.1] mt-1">Tax Preparers</h1>
          <p className="text-[var(--muted)] max-w-[62ch]">
            Preparer profiles saved to your account.
          </p>
        </div>
        <Button size="lg" onClick={addPreparer}>
          + Add Preparer
        </Button>
      </div>

      <Panel className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[var(--muted)]">Loading…</div>
        ) : preparers.length === 0 ? (
          <div className="p-10 text-center text-[var(--muted)]">
            No tax preparers saved yet.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--line)]">
            {preparers.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE).map((p) => (
              <li key={p.id} className="flex items-center justify-between px-5 py-3.5">
                <span className="text-[14px] font-semibold text-[var(--ink)]">{p.name || "Unnamed preparer"}</span>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="ghost"
                    className="!text-xs !px-3 !py-1.5"
                    disabled={editingId === p.id}
                    onClick={() => editPreparer(p)}
                  >
                    {editingId === p.id ? "Loading…" : "Edit →"}
                  </Button>
                  <Button
                    variant="ghost"
                    className="!text-xs !px-3 !py-1.5"
                    disabled={deletingId === p.id}
                    onClick={() => removePreparer(p)}
                  >
                    {deletingId === p.id ? "Deleting…" : "Delete"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {preparers.length > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-[var(--line)]">
            <div className="text-[13px] text-[var(--muted)]">
              Page {page + 1} of {Math.ceil(preparers.length / PAGE_SIZE)}
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="!text-xs !px-3 !py-1.5"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                ← Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="!text-xs !px-3 !py-1.5"
                onClick={() => setPage((p) => Math.min(Math.ceil(preparers.length / PAGE_SIZE) - 1, p + 1))}
                disabled={page >= Math.ceil(preparers.length / PAGE_SIZE) - 1}
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </Panel>

      <ConfirmModal
        open={!!pendingDelete}
        title="Delete Confirmation"
        confirmLabel="Delete"
        loading={deletingId === pendingDelete?.id}
        error={deleteError}
        onConfirm={confirmDeletePreparer}
        onCancel={cancelDeletePreparer}
      >
        Are you sure you want to delete this record? This action cannot be undone.
      </ConfirmModal>
    </div>
  );
};

export default TaxPreparers;
